import { useTranslation } from 'react-i18next'
import { Badge, TableCell, TableRow, TooltipAlertCircle } from '@/ui'
import { REWARD_POINTS_EDIT_PATH } from 'src/layouts/cashback-rewards'
import { useGenerateCompanyPath } from '@/libs'
import { useGameSettingsQuery } from '@/api/useGameSettingsQuery'
import {
  Button,
  DotsHorizontal,
  Menu,
  MenuItem,
  ModalPresetConfirm,
  ModalProps,
  Popper,
  RewardPointOutline,
  useModal,
} from '@dashboard/ui'
import { Disable, Edit03 } from '@/icons'
import { useCurrentUser } from '@/api/useCurrentUser'
import { useNavigate } from 'react-router-dom'
import { useGameSettingsUpdateMutate } from '@/api/useGameSettingsUpdateMutate'

export const RewardPointsRow = () => {
  const { companyId, gameId } = useGenerateCompanyPath()
  const navigate = useNavigate()
  const { data: settings } = useGameSettingsQuery(companyId, gameId)
  const { mutateAsync: updateMutateAsync } = useGameSettingsUpdateMutate(companyId, gameId)
  const { t } = useTranslation('sku-cashback')
  const { t: tMain } = useTranslation()
  const { generatePath } = useGenerateCompanyPath()
  const { canEdit } = useCurrentUser()
  const isActive = settings?.enable_reward_points

  const onConfirm = async () => {
    if (!settings) {
      return
    }

    await updateMutateAsync({
      settingsId: settings.id,
      data: {
        enable_reward_points: !isActive,
      },
    })
  }

  const openDeleteModal = useModal((props: ModalProps) => (
    <ModalPresetConfirm
      {...props}
      title={{
        children: tMain('confirm.title'),
        subtitle: t(isActive ? 'rp.disable.confirm' : 'rp.enable.confirm'),
      }}
      cancel={{ label: tMain('Cancel'), callback: props.onClose }}
      submit={{
        color: isActive ? 'danger' : 'primary',
        label: tMain('yes'),
        callback: () => {
          props.onClose()
          onConfirm()
        },
      }}
    />
  ))

  return (
    <TableRow to={generatePath(REWARD_POINTS_EDIT_PATH)}>
      <TableCell width="100%" className="flex items-center gap-3">
        <div className="rounded-md border border-border-primary bg-fg-primary-alt p-1.5">
          <RewardPointOutline className="text-text-tertiary" />
        </div>
        <div className="text-caption-md font-medium text-text-secondary">{t('table.row.reward-points')}</div>
      </TableCell>
      <TableCell width="60%" className="text-text-disabled">
        <div className="flex items-center gap-1.5">
          {t('table.row.no-linked-item')}
          <TooltipAlertCircle message={t('table.row.no-linked-item.rp')} />
        </div>
      </TableCell>
      <TableCell width="60%" className={!isActive ? 'text-text-disabled' : ''}>
        {isActive ? t('form.rp.desc2') : t('table.row.no-exchange-rate')}
      </TableCell>
      <TableCell width="50%" className="text-text-disabled">
        {t('table.row.system')}
      </TableCell>
      <TableCell width="25%">
        <Badge variant={isActive ? 'lime-primary' : 'gray-secondary'} className="whitespace-nowrap">
          {isActive ? t('table.row.static-status') : t('table.row.static-status.not-active')}
        </Badge>
      </TableCell>
      <TableCell
        width="15%"
        onClick={e => {
          e.stopPropagation()
          e.preventDefault()
        }}
      >
        {canEdit && (
          <Popper
            params={{
              placement: 'bottom-end',
            }}
            popper={props => (
              <Menu {...props}>
                <MenuItem
                  icon={Edit03}
                  onClick={() => {
                    navigate(generatePath(REWARD_POINTS_EDIT_PATH))
                    props.onClose()
                  }}
                >
                  {tMain('edit')}
                </MenuItem>
                <MenuItem
                  variant={isActive ? 'danger' : 'primary'}
                  icon={() => {
                    return <Disable className={isActive ? 'text-text-error-primary' : ''} />
                  }}
                  onClick={() => {
                    openDeleteModal()
                    props.onClose()
                  }}
                >
                  {t(isActive ? 'rp.disable' : 'rp.enable')}
                </MenuItem>
              </Menu>
            )}
          >
            <Button className="w-7 px-0" size="sm" variant="tertiary" color="secondary">
              <DotsHorizontal />
            </Button>
          </Popper>
        )}
      </TableCell>
    </TableRow>
  )
}
